<template>
  <Menubar :model="items" breakpoint="1024px">
    <template #start>
      <router-link
        :to="{ path: '/' }"
        class="t-navbar-item w-16 mr-2 py-1 rounded"
      >
        <img :src="logo" alt="Thyme Care" />
      </router-link>
      <div v-if="authenticated">
        <TextingNotificationsButton />
      </div>
    </template>
    <template #item="{ label, item, props, root, hasSubmenu }">
      <router-link v-if="item.url" v-slot="routerProps" :to="item.url" custom>
        <a :href="item.url" v-bind="props.action" @click="routerProps.navigate">
          <span v-bind="props.icon" />
          <span v-bind="props.label">{{ label }}</span>
          <span
            :class="[
              hasSubmenu &&
                (root ? 'pi pi-fw pi-angle-down' : 'pi pi-fw pi-angle-right'),
            ]"
            v-bind="props.submenuicon"
          />
        </a>
      </router-link>
      <span v-else v-bind="props.action">
        <span v-bind="props.icon" />
        <span v-bind="props.label">{{ label }}</span>
        <span
          :class="[
            hasSubmenu &&
              (root ? 'pi pi-fw pi-angle-down' : 'pi pi-fw pi-angle-right'),
          ]"
          v-bind="props.submenuicon"
        />
      </span>
    </template>
    <template #end>
      <div class="space-x-4">
        <TestVpnButton v-if="showTestVpnButton" class="t-navbar-item" />
        <div v-if="authenticated" class="t-navbar-item">
          <TMTertiaryButton
            label="Log Out"
            :icon-clickable="true"
            icon-pos="left"
            icon="arrowRightCircle"
            class="!text-nash-neutral000"
            @click="logout"
          />
        </div>
        <TMTertiaryButton
          label="Report Issue"
          :icon-clickable="true"
          icon-pos="left"
          icon="wrench"
          class="!text-nash-neutral000"
          @click="isReportIssueModalOpen = true"
        />
        <TMTertiaryButton
          v-if="showExperimentalAIAskSOP"
          label="Ask Thyme Box"
          :icon-clickable="true"
          icon-pos="left"
          icon="sparklesOutline"
          class="!text-nash-neutral000"
          @click="isAskLLMsModalOpen = true"
        />
      </div>
    </template>
  </Menubar>
  <ReportIssueModal
    :is-open="isReportIssueModalOpen"
    @close="isReportIssueModalOpen = false"
  />
  <AskLlmsModal
    :is-open="isAskLLMsModalOpen"
    :show-dev-tools="canUseLlmDevTools"
    :patient-id="patientId"
    @close="isAskLLMsModalOpen = false"
  />
</template>

<script lang="ts">
import { publicAssetUrl } from '@thyme/libs/src/assets/assets'
import TMTertiaryButton from '@thyme/nashville/src/components/button/TMTertiaryButton.vue'
import { MenuItem } from '@thyme/nashville/src/types/navbar'
import { storeToRefs } from 'pinia'
import Menubar from 'primevue/menubar'
import { PropType, computed, defineComponent, ref } from 'vue'
import { useRoute } from 'vue-router'
import { auth0 } from '@/auth'
import ReportIssueModal from '@/legacy/components/reporting/ReportIssueModal/ReportIssueModal.vue'
import TestVpnButton from '@/legacy/components/TestVpnButton.vue'
import { useFlagStore } from '@/store/flags/flags'
import { usePermissions } from '@/store/profile/permissions'
import AskLlmsModal from '@/tokens/llm/AskLlmsModal/AskLlmsModal.vue'
import TextingNotificationsButton from '@/tokens/textingNotifications/TextingNotificationsButton/TextingNotificationsButton.vue'

export default defineComponent({
  components: {
    Menubar,
    TestVpnButton,
    TMTertiaryButton,
    ReportIssueModal,
    TextingNotificationsButton,
    AskLlmsModal,
  },
  props: {
    items: {
      type: Array as PropType<MenuItem[]>,
      default: () => [],
    },
  },
  setup() {
    const isReportIssueModalOpen = ref(false)
    const isAskLLMsModalOpen = ref(false)
    const { showExperimentalAIAskSOP, showTestVpnButton } =
      storeToRefs(useFlagStore())
    const { canUseLlmDevTools } = usePermissions()
    const route = useRoute()
    const patientId = computed(
      () => route.params.patientId as string | undefined
    )

    const logo = publicAssetUrl(
      'assets/images/Thyme-Logo-Wordmark-OffWhite.png'
    )

    /**
     * send logout command to auth0
     */
    async function logout() {
      await auth0().logout()
    }

    return {
      logo,
      authenticated: auth0().isAuthenticated,
      logout,
      isReportIssueModalOpen,
      isAskLLMsModalOpen,
      showExperimentalAIAskSOP,
      canUseLlmDevTools,
      showTestVpnButton,
      patientId,
    }
  },
})
</script>

<style lang="scss">
.t-navbar-item {
  @apply inline-block align-middle relative;
}

a.p-menubar-item-link:hover,
a.p-menubar-item-link:focus,
a.p-menubar-item-link:focus-within,
a.p-menubar-item-link.is-active {
  @apply text-nash-neutral000;
}

.p-menubar-submenu {
  @apply bg-nash-neutral000;

  .p-menubar-item > .p-menubar-item-content {
    @apply text-nash-neutral700;
  }

  .p-menubar-item.p-focus > .p-menubar-item-content,
  .p-menubar-item > .p-menubar-item-content:hover {
    // !important needed to override PrimeVue item.focusBackground/focusColor theme tokens
    @apply bg-nash-neutral100 text-nash-neutral800 !important;
  }

  .p-menubar-item.p-menubar-item-active > .p-menubar-item-content {
    // !important needed to override PrimeVue item.activeBackground theme token
    @apply bg-nash-neutral100 text-nash-neutral700 !important;
  }
}
</style>
